/**
 * MilestonesExecutionCard Component
 *
 * Manager view - Track and manage team milestones
 */

import React from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    IconButton,
    Collapse,
    Chip,
} from '@mui/material';
import { Flag, Add, ExpandMore, Feedback, Edit } from '@mui/icons-material';
import { useOKRDashboardContext } from '../OKRDashboardContext';
import { EmptyState } from '../shared/EmptyState';
import { MilestoneItem } from '../shared/MilestoneItem';
import { getScoreColor } from '../utils/scoreUtils';
import { okrApi } from '../../../services/okrApi';
import { Milestone } from '../../../types';

export const MilestonesExecutionCard: React.FC = () => {
    const { isDarkMode, data, permissions, sections, dialogs, filteredObjectives } = useOKRDashboardContext();
    const { isLocked } = data;
    const { expandedSections, toggleSection } = sections;

    // Only show for managers
    if (!permissions.isManager) {
        return null;
    }

    const handleMilestoneStatusChange = async (milestone: Milestone, newStatus: string) => {
        try {
            await okrApi.updateMilestoneStatus(milestone.id, newStatus);
            // Soft refresh to update scores throughout the hierarchy
            if (data.softRefetchData) {
                await data.softRefetchData();
            }
        } catch (error: any) {
            console.error('Error updating milestone status:', error);
            data.setError(error.message || 'Failed to update milestone status');
        }
    };

    const handleEditMilestone = (milestone: Milestone) => {
        dialogs.openEditMilestoneDialog(milestone);
    };

    const handleDeleteMilestone = async (milestone: Milestone) => {
        if (window.confirm('Are you sure you want to delete this milestone?')) {
            try {
                await okrApi.deleteMilestone(milestone.id);
                data.setSuccess('Milestone deleted successfully');
                if (data.softRefetchData) {
                    await data.softRefetchData();
                }
            } catch (error: any) {
                console.error('Error deleting milestone:', error);
                data.setError(error.message || 'Failed to delete milestone');
            }
        }
    };

    return (
        <Card
            sx={{
                bgcolor: isDarkMode ? 'rgba(255,255,255,0.02)' : '#fff',
                borderRadius: 4,
                boxShadow: isDarkMode
                    ? '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
                    : '0 25px 50px -12px rgba(0, 0, 0, 0.1)',
                border: '1px solid',
                borderColor: isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
            }}
        >
            <CardContent sx={{ p: 0 }}>
                {/* Header */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        cursor: 'pointer',
                        p: { xs: 2.5, sm: 4 },
                        borderBottom: expandedSections.milestonesExecution ? '1px solid' : 'none',
                        borderColor: isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                    }}
                    onClick={() => toggleSection('milestonesExecution')}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 2, sm: 3 }, minWidth: 0 }}>
                        <Box
                            sx={{
                                width: { xs: 44, sm: 56 },
                                height: { xs: 44, sm: 56 },
                                flexShrink: 0,
                                borderRadius: 3,
                                background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.15) 0%, rgba(16, 185, 129, 0.25) 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: '2px solid rgba(34, 197, 94, 0.2)',
                            }}
                        >
                            <Flag sx={{ color: '#22c55e', fontSize: 28 }} />
                        </Box>
                        <Box>
                            <Typography
                                sx={{
                                    fontWeight: 900,
                                    fontSize: '1.25rem',
                                    letterSpacing: '-0.02em',
                                }}
                            >
                                Milestones & Execution
                            </Typography>
                            <Typography
                                sx={{
                                    color: isDarkMode ? '#94a3b8' : '#64748b',
                                    fontWeight: 600,
                                    fontSize: '0.875rem',
                                    mt: 0.5,
                                }}
                            >
                                Track and manage team milestones
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton
                        sx={{
                            bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                            width: 44,
                            height: 44,
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            transform: expandedSections.milestonesExecution ? 'rotate(180deg)' : 'rotate(0deg)',
                        }}
                    >
                        <ExpandMore />
                    </IconButton>
                </Box>

                <Collapse in={expandedSections.milestonesExecution}>
                    <Box sx={{ p: 3 }}>
                        {filteredObjectives.individual.length === 0 ? (
                            <EmptyState
                                icon={<Flag sx={{ fontSize: 48, color: isDarkMode ? '#64748b' : '#94a3b8' }} />}
                                title="No milestones yet"
                                description="Milestones will appear here once tasks are assigned"
                                isDarkMode={isDarkMode}
                            />
                        ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                {filteredObjectives.individual.map((obj: any) => {
                                    const scoreColor = getScoreColor(obj.score || 0);
                                    const milestones = obj.milestones || [];

                                    return (
                                        <Box
                                            key={obj.id}
                                            sx={{
                                                p: 3,
                                                borderRadius: 3,
                                                bgcolor: isDarkMode ? '#1e293b40' : '#f8fafc',
                                                border: `1px solid ${isDarkMode ? '#334155' : '#e2e8f0'}`,
                                            }}
                                        >
                                            {/* Objective Header */}
                                            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'flex-start' }, gap: { xs: 1.5, sm: 0 }, mb: 2 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
                                                    <Box
                                                        sx={{
                                                            width: 44,
                                                            height: 44,
                                                            borderRadius: 2,
                                                            bgcolor: `${scoreColor}15`,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            border: `2px solid ${scoreColor}30`,
                                                            flexShrink: 0,
                                                        }}
                                                    >
                                                        <Flag sx={{ color: scoreColor, fontSize: 24 }} />
                                                    </Box>
                                                    <Box sx={{ minWidth: 0 }}>
                                                        <Typography
                                                            sx={{
                                                                fontWeight: 700,
                                                                fontSize: '1rem',
                                                                color: isDarkMode ? '#f1f5f9' : '#1e293b',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                whiteSpace: 'nowrap',
                                                            }}
                                                        >
                                                            {obj.title}
                                                        </Typography>
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                color: isDarkMode ? '#64748b' : '#94a3b8',
                                                            }}
                                                        >
                                                            {obj.employee_name || 'Unassigned'} • {obj.team_title || 'Team Objective'}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', flexShrink: 0 }}>
                                                    <Chip
                                                        label={`${Math.round(obj.score || 0)}%`}
                                                        size="small"
                                                        sx={{
                                                            bgcolor: scoreColor,
                                                            color: '#fff',
                                                            fontWeight: 700,
                                                            fontSize: '0.8rem',
                                                            boxShadow: `0 2px 6px ${scoreColor}40`,
                                                        }}
                                                    />
                                                    {!isLocked && (
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => dialogs.openEditIndObjDialog(obj)}
                                                            sx={{
                                                                color: isDarkMode ? '#94a3b8' : '#64748b',
                                                                '&:hover': {
                                                                    bgcolor: isDarkMode ? '#334155' : '#e2e8f0',
                                                                },
                                                            }}
                                                        >
                                                            <Edit fontSize="small" />
                                                        </IconButton>
                                                    )}
                                                    {!isLocked && permissions.canAssignMilestones && (
                                                        <Button
                                                            size="small"
                                                            startIcon={<Add />}
                                                            onClick={() => dialogs.openAddMilestoneDialog(obj.id)}
                                                            sx={{
                                                                bgcolor: 'rgba(34, 197, 94, 0.15)',
                                                                color: '#22c55e',
                                                                fontWeight: 600,
                                                                fontSize: '0.75rem',
                                                                textTransform: 'none',
                                                                '&:hover': {
                                                                    bgcolor: 'rgba(34, 197, 94, 0.25)',
                                                                },
                                                            }}
                                                        >
                                                            Add Milestone
                                                        </Button>
                                                    )}
                                                    {permissions.canAddFeedback && (
                                                        <Button
                                                            size="small"
                                                            startIcon={<Feedback />}
                                                            onClick={() => dialogs.openFeedbackDialog(obj)}
                                                            sx={{
                                                                bgcolor: 'rgba(245, 158, 11, 0.15)',
                                                                color: '#f59e0b',
                                                                fontWeight: 600,
                                                                fontSize: '0.75rem',
                                                                textTransform: 'none',
                                                                '&:hover': {
                                                                    bgcolor: 'rgba(245, 158, 11, 0.25)',
                                                                },
                                                            }}
                                                        >
                                                            Feedback
                                                        </Button>
                                                    )}
                                                </Box>
                                            </Box>

                                            {/* Milestones List */}
                                            {milestones.length > 0 ? (
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
                                                    {milestones.map((milestone: any) => (
                                                        <MilestoneItem
                                                            key={milestone.id}
                                                            milestone={milestone}
                                                            isDarkMode={isDarkMode}
                                                            showActions={permissions.isManager}
                                                            onStatusChange={!isLocked ? handleMilestoneStatusChange : undefined}
                                                            onEdit={!isLocked ? handleEditMilestone : undefined}
                                                            onDelete={!isLocked ? handleDeleteMilestone : undefined}
                                                        />
                                                    ))}
                                                </Box>
                                            ) : (
                                                <Typography
                                                    sx={{
                                                        color: isDarkMode ? '#64748b' : '#94a3b8',
                                                        fontSize: '0.85rem',
                                                        fontStyle: 'italic',
                                                        mt: 2,
                                                    }}
                                                >
                                                    No milestones defined yet
                                                </Typography>
                                            )}
                                        </Box>
                                    );
                                })}
                            </Box>
                        )}
                    </Box>
                </Collapse>
            </CardContent>
        </Card>
    );
};

export default MilestonesExecutionCard;
